import type { LucideIcon } from "lucide-react";
import { type Variants, motion } from "motion/react";
import React from "react";

import { cn } from "~/lib/cn";
import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";

/**
 * EmptyState -- the canonical "nothing here yet" surface.
 *
 * Two looks, one component:
 *  - `default`  a calm, refined treatment for everyday empty lists
 *               (no invoices, no notes yet). Neutral icon tile, soft motion.
 *  - `hero`     a branded, aspirational moment for first-run / zero-data
 *               screens (no coaching programs yet, find a coach). Soft purple
 *               glow, layered rings, a gradient icon tile.
 *
 * Compose calls-to-action through `children` (typically a <Button> /
 * <ButtonRouter>). Motion is staggered and respects prefers-reduced-motion.
 */

export type EmptyStateVariant = "default" | "hero";
export type EmptyStateSize = "sm" | "md" | "lg";

const SIZES: Record<
  EmptyStateSize,
  {
    pad: string;
    tile: string;
    icon: string;
    gap: string;
    maxW: string;
    title: string;
    body: string;
  }
> = {
  sm: {
    pad: "py-8",
    tile: "size-11 rounded-xl",
    icon: "size-5",
    gap: "gap-2.5",
    maxW: "max-w-xs",
    title: "text-sm",
    body: "text-xs",
  },
  md: {
    pad: "py-12",
    tile: "size-14 rounded-2xl",
    icon: "size-6",
    gap: "gap-3",
    maxW: "max-w-sm",
    title: "text-base",
    body: "text-sm",
  },
  lg: {
    pad: "py-16",
    tile: "size-16 rounded-2xl",
    icon: "size-7",
    gap: "gap-4",
    maxW: "max-w-md",
    title: "text-lg",
    body: "text-sm",
  },
};

/** The standard "nothing here yet" block with an icon, title, description and actions. */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  children,
  className,
  variant = "default",
  size = "md",
  ...props
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  variant?: EmptyStateVariant;
  size?: EmptyStateSize;
}) => {
  const reduce = useReducedMotionSafe();
  const s = SIZES[size];
  const isHero = variant === "hero";

  const container: Variants = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.07, delayChildren: 0.04 },
    },
  };
  const item: Variants = {
    hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 8 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: [0.19, 1, 0.22, 1] },
    },
  };

  const content = (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className={cn(
        "relative z-10 flex w-full flex-col items-center text-center",
        s.gap,
        s.maxW,
      )}
    >
      {Icon && (
        <motion.div variants={item}>
          <motion.div
            animate={reduce ? undefined : { scale: [1, 1.03, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className={cn(
              "flex items-center justify-center",
              s.tile,
              isHero
                ? "bg-gradient-to-b from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/30"
                : "bg-gradient-to-b from-white to-gray-50 shadow-sm ring-1 ring-gray-200/70",
            )}
          >
            <Icon
              className={cn(
                s.icon,
                isHero ? "text-white" : "text-text-tertiary",
              )}
            />
          </motion.div>
        </motion.div>
      )}

      <div className="space-y-1">
        <motion.h3
          variants={item}
          className={cn(
            "text-text-primary font-heading font-medium tracking-tight text-balance",
            s.title,
          )}
        >
          {title}
        </motion.h3>
        {description && (
          <motion.p
            variants={item}
            className={cn(
              "text-text-secondary mx-auto leading-relaxed text-pretty",
              s.body,
            )}
          >
            {description}
          </motion.p>
        )}
      </div>

      {children && (
        <motion.div
          variants={item}
          className="mt-2 flex flex-wrap items-center justify-center gap-2"
        >
          {children}
        </motion.div>
      )}
    </motion.div>
  );

  if (isHero) {
    return (
      <div
        className={cn("flex items-center justify-center", s.pad, className)}
        {...props}
      >
        <div className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-gradient-to-b from-brand-50/70 via-white to-white px-6 py-14 ring-1 ring-brand-100/70">
          {/* soft brand glow behind the icon */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 left-1/2 size-48 -translate-x-1/2 rounded-full bg-brand-400/20 blur-3xl"
          />
          {/* faint concentric rings, à la the brand CTA banner */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 left-1/2 size-72 -translate-x-1/2 rounded-full border border-brand-500/10"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 left-1/2 size-[28rem] -translate-x-1/2 rounded-full border border-brand-500/5"
          />
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("flex items-center justify-center", s.pad, className)}
      {...props}
    >
      {content}
    </div>
  );
};
